import React, { useState, useEffect, useMemo } from 'react';
import '../../styles/modal.css';
import axiosInstance from '../../api/axiosInstance';
import { AccessibleSelect } from '../common';

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const EXECUTION_MODE_OPTIONS = [
  { value: '1', label: 'Parallel' },
  { value: '2', label: 'Sequence' },
];

const CreateTestSuiteModal = ({ isOpen, onClose, onCreateTestSuite, projects, defaultProjectId }) => {
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    suite_name: "",
    project_id: "",
    description: "",
    enable_automated_execution: false,
    enable_reporting: false,
    enable_notification: false,
    execution_mode: "",
    environment_id: ""
  });
  const [environments, setEnvironments] = useState([]);
  const [environmentsLoading, setEnvironmentsLoading] = useState(false);

  const resolvedProjectId = projects?.project_id || defaultProjectId || "";
  const hasPreselectedProject = Boolean(resolvedProjectId);
  const preselectedProjectName =
    projects?.project_name || projects?.name || "";

  const projectOptions = useMemo(() => {
    if (!projects?.project_id) return [];
    return [
      {
        value: String(projects.project_id),
        label: preselectedProjectName || String(projects.project_id),
      },
    ];
  }, [projects, preselectedProjectName]);

  const environmentOptions = useMemo(
    () =>
      environments.map((env) => ({
        value: String(env.id),
        label: env.environment_name,
      })),
    [environments]
  );

  const selectedEnvironment = environments.find(
    (env) => String(env.id) === String(formData.environment_id)
  );

  const getEmptyFormData = (projectId = "") => ({
    suite_name: "",
    project_id: projectId ? String(projectId) : "",
    description: "",
    enable_automated_execution: false,
    enable_reporting: false,
    enable_notification: false,
    execution_mode: "",
    environment_id: ""
  });

  useEffect(() => {
    if (!isOpen) return;

    setFormData(getEmptyFormData(resolvedProjectId));
    setError(null);
  }, [isOpen, resolvedProjectId]);

  useEffect(() => {
    const projectId = formData.project_id || resolvedProjectId;
    if (!isOpen || !projectId) {
      setEnvironments([]);
      return;
    }

    const fetchEnvironments = async () => {
      try {
        setEnvironmentsLoading(true);
        const response = await axiosInstance.get(
          `/user_profile/list_environments?project_id=${projectId}`
        );

        if (response.data && response.status_code === 200) {
          setEnvironments(response.data || []);
        } else {
          console.error('Failed to fetch environments:', response);
          setEnvironments([]);
        }
      } catch (err) {
        console.error('Error fetching environments:', err);
        setEnvironments([]);
      } finally {
        setEnvironmentsLoading(false);
      }
    };

    fetchEnvironments();
  }, [isOpen, formData.project_id, resolvedProjectId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev =>
      ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleFieldChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form
    if (!formData.suite_name.trim()) {
      setError('Test suite name is required');
      return;
    }
    if (!formData.project_id) {
      setError('Please select a project');
      return;
    }
    if (!formData.environment_id) {
      setError('Please select an environment');
      return;
    }
    if (!formData.execution_mode) {
      setError('Please select execution mode');
      return;
    }

    const newTestSuite = {
      suite_name: formData.suite_name.trim(),
      project_id: parseInt(formData.project_id, 10),
      description: formData.description,
      enable_automated_execution: formData.enable_automated_execution,
      enable_reporting: formData.enable_reporting,
      enable_notification: formData.enable_notification,
      environment_id: parseInt(formData.environment_id, 10),
      execution_mode: formData.execution_mode,
    };
    submitTestSuit(newTestSuite)

  };

  const submitTestSuit=async (requestBody)=>{
  try{
  const projectsRes = await axiosInstance.post('/test_suite/add_suite', requestBody);
  setFormData({
    suite_name: "",
    project_id: "",
    description: "",
    enable_automated_execution: false,
    enable_reporting: false,
    enable_notification: false,
    execution_mode: ""
  });
  onClose();
}catch(error){
  setFormData(getEmptyFormData());
  onClose();
}
  }
  if (!isOpen) return null;

  return (
    <>
      <div data-rt-modal-overlay className="modal-overlay create-test-suite-modal-overlay" onClick={() => onClose(true)}></div>
      <div className="modal create-test-suite-modal" style={{ width: '50vw', height: '70vh' }}>
        <div className="modal-header">
          <h2>Create New Test Suite</h2>
          <button type="button" className="modal-close" onClick={() => onClose(true)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
              viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <form className="create-test-suite-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="suite_name">Test Suite Name</label>
              <input
                type="text"
                id="suite_name"
                name="suite_name"
                maxLength={100}
                placeholder="Enter test suite name"
                className="form-input"
                value={formData.suite_name}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                placeholder="Enter test suite description"
                className="form-input"
                rows="4"
                value={formData.description}
                onChange={handleChange}
              ></textarea>
            </div>

            {hasPreselectedProject ? (
              <div className="form-group">
                <label htmlFor="project_id">Project</label>
                <input
                  type="text"
                  id="project_id"
                  className="form-input"
                  value={preselectedProjectName}
                  readOnly
                  disabled
                />
              </div>
            ) : (
              <div className="form-group">
                <label id="project_id-label" htmlFor="project_id">Project</label>
                <AccessibleSelect
                  id="project_id"
                  value={formData.project_id}
                  onChange={(value) => handleFieldChange('project_id', value)}
                  options={projectOptions}
                  placeholder="Select a project"
                  aria-labelledby="project_id-label"
                />
              </div>
            )}

            <div className="form-group">
              <label id="environment-label" htmlFor="environment_id">
                Environment <span style={{ color: 'red' }}>*</span>
              </label>
              <AccessibleSelect
                id="environment_id"
                value={formData.environment_id}
                onChange={(value) => handleFieldChange('environment_id', value)}
                options={environmentOptions}
                placeholder={
                  environmentsLoading ? 'Loading...' : 'Select Environment'
                }
                disabled={environmentsLoading || !formData.project_id}
                required
                error={Boolean(error && !formData.environment_id)}
                aria-labelledby="environment-label"
              />
              {selectedEnvironment && (
                <p style={{ fontSize: '0.85rem', color: '#28a745', marginTop: '4px' }}>
                  ✓ URL: {selectedEnvironment.target_url}
                </p>
              )}
            </div>

            <div className="form-group">
              <label id="execution_mode-label" htmlFor="execution_mode">
                Execution Mode <span style={{ color: 'red' }}>*</span>
              </label>
              <AccessibleSelect
                id="execution_mode"
                value={formData.execution_mode}
                onChange={(value) => handleFieldChange('execution_mode', value)}
                options={EXECUTION_MODE_OPTIONS}
                placeholder="Select Execution Mode"
                required
                aria-labelledby="execution_mode-label"
              />
            </div>

            <div className="form-group">
              <label>Test Suite Settings</label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="enable_automated_execution"
                    checked={formData.enable_automated_execution}
                    onChange={handleChange}
                  /> <span>Enable Automated Execution</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="enable_reporting"
                    checked={formData.enable_reporting}
                    onChange={handleChange}
                  /> <span>Enable Reporting</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="enable_notification"
                    checked={formData.enable_notification}
                    onChange={handleChange}
                  /> <span>Enable Notifications</span>
                </label>
              </div>
            </div>

            {error && <div className="form-error">{error}</div>}
          </form>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={() => onClose(true)}>Cancel</button>
          <button type="button" className="btn-primary" onClick={handleSubmit}>Create Test Suite</button>
        </div>
      </div>
    </>
  );
};

export default CreateTestSuiteModal;
